import { localeDefinitions, type Locale } from '@/i18n/locales';
import Head from 'next/head';

import type { SeoGuideArticle } from '@/lib/seo/guides';
import {
  SEARCH_PREVIEW_IMAGE,
  SITE_NAME,
  buildLocalizedSeoPath,
  buildAbsoluteSeoImageUrl,
  getSeoSiteUrl,
} from '@/lib/seo/site';
import enMessages from '../../../messages/en.json';
import zhMessages from '../../../messages/zh.json';

interface StaticSeoGuideHeadProps {
  locale: Locale;
  translatedMetadata?: { title: string; description: string; socialDescription: string };
  guide?: SeoGuideArticle;
}

const BASE_URL = getSeoSiteUrl();
const metadataByLocale = {
  en: enMessages.seoGuides.metadata,
  zh: zhMessages.seoGuides.metadata,
};

export function StaticSeoGuideHead({ locale, guide, translatedMetadata }: StaticSeoGuideHeadProps) {
  const metadata = translatedMetadata ?? metadataByLocale[locale === 'zh' ? 'zh' : 'en'];
  const path = guide?.path ?? '/guides';
  const title = guide ? (guide.metadataTitle ?? `${guide.title} | ${SITE_NAME}`) : metadata.title;
  const description = guide?.description ?? metadata.description;
  const socialDescription = guide?.description ?? metadata.socialDescription;
  const canonicalUrl = `${BASE_URL}${buildLocalizedSeoPath(path, locale)}`;
  const image = guide?.heroImage ?? SEARCH_PREVIEW_IMAGE;

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      {guide ? <meta name="keywords" content={`${guide.primaryKeyword}, ${guide.searchIntent}, DOBIDOBI, AI fashion visuals`} /> : null}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:type" content={guide ? 'article' : 'website'} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={buildAbsoluteSeoImageUrl(image)} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={buildAbsoluteSeoImageUrl(image)} />
      <link rel="canonical" href={canonicalUrl} />
      {Object.entries(localeDefinitions).map(([key, value]) => (
        <link key={key} rel="alternate" hrefLang={value.html} href={`${BASE_URL}${buildLocalizedSeoPath(path, key as Locale)}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${path}`} />
      <link rel="icon" type="image/png" sizes="512x512" href="/icon.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/apple-icon.png" />
    </Head>
  );
}
